import React, { useEffect, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import { RMap, RMapContextProvider } from "maplibre-react-components";
import CoefficientLayer from "@/components/Map/CoefficientLayer";
import CenterPinLayer from "@/components/Map/Pin";
import UserLocationMarker from "@/components/Map/UserLocation";
import MapController from "@/components/Map/ControlButtons";
import MapHeader from "@/components/Map/Header";
import { initialLocationCenter } from "@/utils/helpers";
import LastUpdateTime from "@/components/Map/LastUpdate";
import Watermarks from "@/components/Map/Watermarks";
import { useAdminToolsDataContext } from "@/store/AdminToolsProvider";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { useSelectedHexGroupByPin } from "@/utils/hooks/useSelectedHexGroupByPin";
import HexBorderLayer from "@/components/Map/HexBorderLayer";
import AgglomerationsLayer from "@/components/Map/AgglomerationsLayer";
import PriceLabelsLayer from "@/components/Map/PriceLabelsLayer";

const CENTER: [number, number] = [37.6151, 55.7569];
const INITIAL_ZOOM = 1;
const TARGET_ZOOM = 12;

const PRIMARY_STYLE = "https://tiles.openfreemap.org/styles/liberty";
const FALLBACK_STYLE = "/assets/mapStyle.json";

const CARTO_TILEJSON =
  "https://tiles.basemaps.cartocdn.com/vector/carto.streets/v1/tiles.json";

const HexGroupByPinController: React.FC = () => {
  useSelectedHexGroupByPin("main-map");
  return null;
};

const Map = () => {
  const selectedHexGroup = useSelectedHexGroupStore((s) => s.selectedHexGroup);
  const { isAdmin } = useAdminToolsDataContext();

  const [resolvedStyle, setResolvedStyle] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const timeoutId = window.setTimeout(() => ctrl.abort(), 2000);

    fetch(CARTO_TILEJSON, { signal: ctrl.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`Bad status: ${r.status}`);
        if (!cancelled) setResolvedStyle(PRIMARY_STYLE);
      })
      .catch(() => {
        if (!cancelled) setResolvedStyle(FALLBACK_STYLE);
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearTimeout(timeoutId);
    };
  }, []);
  const selectedHexGroupCenter: [number, number] =
    selectedHexGroup?.center?.lat && selectedHexGroup?.center?.lon
      ? [selectedHexGroup.center.lon, selectedHexGroup.center.lat]
      : CENTER;

  const finalCenter: [number, number] =
    initialLocationCenter() || selectedHexGroupCenter;

  if (!resolvedStyle) {
    return <div style={{ width: "100%", height: "100%" }} />;
  }

  return (
    <RMapContextProvider>
      <RMap
        id="main-map"
        initialCenter={finalCenter}
        initialZoom={INITIAL_ZOOM}
        style={{ width: "100%", height: "100%" }}
        minZoom={isAdmin ? undefined : 9.5}
        mapStyle={resolvedStyle}
        onMounted={(map) => {
          map.dragRotate.disable();
          map.touchZoomRotate.disableRotation();
          map.showTileBoundaries = false;

          map.once("load", () => {
            setMapLoaded(true);

            map.flyTo({
              center: finalCenter,
              zoom: TARGET_ZOOM,
              speed: 0.8,
              curve: 1.5,
              essential: true,
            });

            map.once("moveend", () => {
              map.setMinZoom(3);
            });
          });
        }}
      >
        <HexGroupByPinController />

        <MapHeader />

        {mapLoaded && <CoefficientLayer />}
        {mapLoaded && <HexBorderLayer />}
        {mapLoaded && <PriceLabelsLayer />}
        {mapLoaded && <AgglomerationsLayer />}
        {mapLoaded && <CenterPinLayer mapId="main-map" />}
        {mapLoaded && <UserLocationMarker />}
        {mapLoaded && <MapController />}

        <LastUpdateTime />
        <Watermarks />
      </RMap>
    </RMapContextProvider>
  );
};

export default Map;
